/* eslint-disable radix */
import React, { useEffect } from 'react';

import _ from 'lodash';
import { TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { makeStyles } from 'tss-react/mui';

import '../OhsFormField.css';
import { OhsTime } from 'global-services/constants/OhsTimeModels';

import OhsFormFieldLayout from '../components/OhsFormFieldLayout';
import { returnFieldStyle, returnVariant } from '../components/OhsFieldLayout';
import { OhsFormFieldBaseProps } from '../services/OhsFormModels';

export const useStyles = makeStyles()(() => ({
  container: {
    position: 'relative',
  },
  closeContainer: {
    top: '0px',
    bottom: '0px',
    left: '0px',
    right: '0px',
    position: 'absolute',
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  clock: {
    marginLeft: '12px',
    fontSize: '1.5em',
    opacity: 0.75,
    pointerEvents: 'none',
  },
  close: {
    marginLeft: 'auto',
    marginRight: '12px',
    fontSize: '1.25em',
    opacity: 0.5,
    cursor: 'pointer',
    pointerEvents: 'auto',
  },
  input: {
    paddingLeft: '46px',
    '::-webkit-calendar-picker-indicator': {
      background: 'transparent',
      bottom: 0,
      color: 'transparent',
      cursor: 'pointer',
      height: 'auto',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      width: 'auto',
    },
  },
}));

export interface Props extends OhsFormFieldBaseProps {}

const formatTimeToObj = (time: string | null): OhsTime | null => {
  if (time === '' || time == null) {
    return null;
  }
  if (time.split) {
    const timeParts = time.split(':');
    return new OhsTime(parseInt(timeParts[0], 10), parseInt(timeParts[1], 0));
  }
  return null;
};

const formatObjTimeToString = (time: OhsTime | null): string | null => {
  if (time?.hours != null && time?.minutes != null) {
    return `${time.hours > 9 ? time.hours : `0${time.hours}`}:${
      time.minutes > 9 ? time.minutes : `0${time.minutes}`
    }`;
  }
  return null;
};

function InputEle(props: Props) {
  const { classes } = useStyles();
  const inputRef = props.ohsFormSettings.useFormMethods.register(props.id).ref;
  const [value, setValue] = React.useState<OhsTime | null>(null);
  const displayValue = formatObjTimeToString(value);

  const propValue = props.ohsFormSettings.useFormMethods.watch(props.id);

  const error = _.get(props.ohsFormSettings.useFormMethods.formState.errors, props.id);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const formattedValue = formatTimeToObj(newValue);
    setValue(formattedValue);
    props.ohsFormSettings.useFormMethods.setValue(props.id, formattedValue || '');
    props.ohsFormSettings.useFormMethods.trigger(props.id);
  };

  const handleClear = () => {
    setValue(null);
    props.ohsFormSettings.useFormMethods.setValue(props.id, '');
  };

  /*
    empty propValue can't be null, only ''
    empty value cant be '', only null
  */
  useEffect(() => {
    if (propValue !== value) {
      if ((propValue === '' || propValue === undefined) && value !== null) {
        setValue(null);
      } else {
        setValue(propValue || null);
      }
    }
  }, [propValue, value]);

  useEffect(() => {
    props.ohsFormSettings.useFormMethods.register(props.id, {
      validate: {
        notEmpty: (val: OhsTime | null | string) => {
          if (props.required === true) {
            if (val === undefined || val === null || val === '') return false;
          }

          return true;
        },
      },
    });
  }, [
    props.id,
    props.ohsFormSettings.useFormMethods,
    props.ohsFormSettings.useFormMethods.register,
    props.required,
  ]);

  return (
    <div className={classes.container}>
      <TextField
        ref={inputRef}
        type="time"
        error={error != null}
        fullWidth
        size="small"
        margin="dense"
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{
          'data-testid': props.id,
          className: classes.input,
        }}
        variant={returnVariant(props.ohsFormSettings.layout)}
        required={props.required}
        disabled={props.disabled}
        defaultValue={null}
        onChange={handleOnChange}
        value={displayValue}
        style={returnFieldStyle(props.ohsFormSettings.layout)}
      />
      <div className={classes.closeContainer}>
        <AccessTimeIcon className={classes.clock} />
        {!_.isNil(value) && <CloseIcon className={classes.close} onClick={handleClear} />}
      </div>
    </div>
  );
}

export default function OhsTimeField(props: Props) {
  return <OhsFormFieldLayout {...props} inputEle={<InputEle {...props} />} />;
}
